import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { compose } from "redux";
import actionCreators from "@modules/service/actionCreator";
import * as serviceSelectors from "@modules/service/selectors";
import { Button, Pagination, Loader, GSGrid, View } from "@components";
import type { State } from "@store/reducers";
import type {
  AbsenceAuditLog,
  AuditLogsFilterType,
} from "@modules/service/reducer/audit-logs/types";
import { type PaginationExtendedType } from "@types";
import { route } from "@routes/helper";
import { AuditLogsFilter } from "@modules/shared/components";
import moment from "moment";

type PropsFromState = {
  isLoading: boolean,
  list: Array<AbsenceAuditLog>,
  pagination: PaginationExtendedType,
};

type PropsFromDispatch = {
  getNotificationAuditLogs: typeof actionCreators.GetNotificationAuditLogs,
  exportNotificationAuditLogs: typeof actionCreators.ExportNotificationAuditLogs,
};

type Props = {
  ...PropsFromState,
  ...PropsFromDispatch,
  ...PropsFromLocalize,
  clientId: number,
  showADDModal: boolean,
  showEDITModal: boolean,
};

export function NotificationAuditLogs(props: Props) {
  const { list, pagination, clientId, t } = props;
  const [searchParams, setSearchParams]: [?AuditLogsFilterType, Function] = useState({
    clientId,
    search: {
      dateFrom: null,
      dateTo: null,
      userId: null,
      employeeId: null,
    },
    page: 1,
  });

  useEffect(() => {
    props.getNotificationAuditLogs(searchParams);
  }, [clientId]);

  const getActionPillColor = (action: string) => {
    const colors = {
      Added: "background-color-mint",
      Removed: "background-color-berry",
      Changed: "background-color-coffee",
    };

    return colors[action] || "background-color-coffee";
  };

  const columns = [
    {
      name: t("USER_ID"),
      key: "userId",
      align: "left",
      width: "fit-content",
      renderCell: ({ row }: Object) => row.userId,
    },
    {
      name: t("USER"),
      align: "left",
      key: "userFullName",
    },
    {
      name: t("ACTIVITY_DATE"),
      key: "activityDate",
      width: "fit-content",
      renderCell: ({ row }: Object) => GSGrid.render.date(row.activityDate),
    },
    {
      name: t("EMPLOYEE_ID"),
      key: "targetEmployeeId",
      align: "left",
      width: "fit-content",
    },
    {
      name: t("EMPLOYEE_RECORD"),
      key: "targetEmployeeFullName",
      align: "left",
    },
    {
      name: t("ACTION"),
      key: "action",
      width: "fit-content",
      renderCell: ({ row }: Object) =>
        GSGrid.render.statusPill(
          row.action,
          null,
          `text-color-black ${getActionPillColor(row.action)}`,
        ),
    },
    {
      name: t("CONTACT"),
      key: "recipientEmployeeFullName",
      align: "left",
      width: "max-content",
    },
    {
      name: t("TYPE"),
      key: "notificationType",
      width: "fit-content",
    },
    {
      name: t("VIEW"),
      key: props.t("VIEW"),
      width: 40,
      align: "center",
      onCellClick: (row: Object) => {
        window.open(
          route("REPORTING_EMPLOYEE_NOTIFICATIONS_PATH", {
            clientId: row.clientId,
            employeeId: row.targetEmployeeId,
          }),
          "_blank",
        );
      },
      renderCell: ({ row }: Object) => GSGrid.render.materialIcon("play_circle"),
    },
  ];

  function handleSubmitFilter(e: Event) {
    e.preventDefault();
    props.getNotificationAuditLogs({ ...searchParams, page: 1 });
  }

  function handleFilterChange(data) {
    setSearchParams({
      ...searchParams,
      search: {
        ...data,
        // TODO - this ugly custom fix, remove it when the BE respect time zones +02:00
        dateFrom: moment(data.dateFrom)
          .set("hour", 0)
          .set("minute", 0)
          .set("second", 0)
          .utc()
          .toISOString(),
        dateTo: moment(data.dateTo)
          .set("hour", 23)
          .set("minute", 59)
          .set("second", 59)
          .utc()
          .toISOString(),
      },
    });
  }

  function handleExport(e: Event) {
    e.preventDefault();
    props.exportNotificationAuditLogs(searchParams);
  }

  function handleChangePage(page: string) {
    const res = {
      ...searchParams,
      page: parseInt(page),
    };

    setSearchParams(res);
    props.getNotificationAuditLogs(res);
  }

  return (
    <View>
      {props.isLoading && <Loader />}
      <View justifyContent="space-between" direction="row" margin={[0, 0, "1rem", 0]}>
        <View direction="row" gap={4} justifyContent="center" alignItems="flex-end">
          <AuditLogsFilter
            handleSubmit={handleSubmitFilter}
            onChange={handleFilterChange}
            data={searchParams.search}
          />

          <Button materialIcon="replay_circle_filled" onClick={handleSubmitFilter}>
            {props.t("SUBMIT")}
          </Button>
        </View>
        <View direction="row" justifyContent="center" alignItems="flex-end">
          <Button materialIcon="file_download" onClick={handleExport}>
            {t("EXPORT")}
          </Button>
        </View>
      </View>

      {list.length ? (
        <GSGrid columns={columns} rows={list} />
      ) : (
        <div className="my-5 d-flex justify-content-center">
          <h3 className="text-pinkish-grey">{t("NOTIFICATION_AUDITS_EMPTY")}</h3>
        </div>
      )}
      {pagination && (
        <Pagination
          className="my-3 d-flex flex-fill justify-content-center"
          currentPage={pagination.pageNumber}
          totalPages={pagination.totalPages}
          onChange={handleChangePage}
        />
      )}
    </View>
  );
}

const mapStateToProps = (state: State): PropsFromState => {
  return {
    list: serviceSelectors.selectNotificationAuditLogsList(state),
    pagination: serviceSelectors.selectNotificationAuditLogsPagination(state),
    isLoading: serviceSelectors.isLoadingAuditLogs(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getNotificationAuditLogs: actionCreators.GetNotificationAuditLogs,
  exportNotificationAuditLogs: actionCreators.ExportNotificationAuditLogs,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(NotificationAuditLogs);
